import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import { cn } from "@/lib/utils"
import { CloseIcon as XIcon } from './close-icon';

/**
 * Dialog wrapper that makes the system Back button (Android, browser, swipe)
 * close the dialog instead of navigating away from the underlying page.
 *
 * Strategy:
 *   - When Dialog opens, push a synthetic history entry.
 *   - Listen for `popstate` (Back press) → close the dialog instead of
 *     letting the browser navigate.
 *   - When Dialog closes via UI (X button, outside-click, Esc) → pop our
 *     synthetic entry so forward-history stays clean.
 *
 * This wraps Radix's <Dialog.Root> transparently — every existing
 * <Dialog open={x} onOpenChange={setX}> in the app automatically gets
 * Back-button awareness with no changes at the call site.
 */
const Dialog = ({ open, defaultOpen, onOpenChange, ...props }) => {
  // Track previous open state so we know when we're transitioning
  const prevOpenRef = React.useRef(open === true || defaultOpen === true);
  // Did WE push a history entry that we still need to clean up?
  const pushedRef = React.useRef(false);
  // Is a Back-press currently closing us? (so we don't pop again)
  const closingViaPopRef = React.useRef(false);

  // Wrap onOpenChange so we can clean up the synthetic history entry when
  // the dialog closes via UI (X / outside click / Esc).
  const handleOpenChange = React.useCallback((next) => {
    if (!next && pushedRef.current && !closingViaPopRef.current) {
      // UI close → pop our synthetic entry so user's next Back fully leaves.
      pushedRef.current = false;
      try { window.history.back(); } catch (_) { /* noop */ }
    }
    closingViaPopRef.current = false;
    if (typeof onOpenChange === 'function') onOpenChange(next);
  }, [onOpenChange]);

  // React to open/close transitions
  React.useEffect(() => {
    const wasOpen = prevOpenRef.current;
    const nowOpen = open === true;
    prevOpenRef.current = nowOpen;

    if (nowOpen && !wasOpen) {
      // OPENING: push synthetic history entry
      try {
        window.history.pushState({ __dialogOpen: true, t: Date.now() }, '');
        pushedRef.current = true;
      } catch (_) { pushedRef.current = false; }

      const onPop = () => {
        // Back was pressed while dialog was open — the synthetic entry has
        // already been popped by the browser, so just close the dialog.
        if (pushedRef.current) {
          pushedRef.current = false;
          closingViaPopRef.current = true;
          if (typeof onOpenChange === 'function') onOpenChange(false);
        }
      };
      window.addEventListener('popstate', onPop);
      return () => window.removeEventListener('popstate', onPop);
    }
  }, [open, onOpenChange]);

  // Cleanup on unmount: pop the synthetic entry ONLY if we still own the
  // top of the history stack. If the user has navigated forward (e.g. by
  // clicking a <Link> inside the dialog), our synthetic entry is no longer
  // on top — calling history.back() here would undo the user's navigation
  // and send them to the page underneath the dialog.
  React.useEffect(() => {
    return () => {
      if (pushedRef.current) {
        pushedRef.current = false;
        try {
          if (window.history.state && window.history.state.__dialogOpen) {
            window.history.back();
          }
        } catch (_) { /* noop */ }
      }
    };
  }, []);

  return (
    <DialogPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      {...props}
    />
  );
};
Dialog.displayName = 'Dialog';

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <XIcon className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
